import { useState, useEffect, useCallback } from 'react';
import {
  Bell, AlertTriangle, CheckCircle, ChevronRight, Clock,
  Filter, Loader2, Plus, RefreshCw, Search, XCircle, Check,
  Activity, Radio, Shield, Wifi, WifiOff, Eye, Pause, Play,
  Trash2, Copy, Settings, Edit2, Zap, Target, Send, Link2,
  Download, Power, Mail, Hash, Smartphone, ArrowUpCircle, HelpCircle} from 'lucide-react';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import operationsTutorial from '../components/tutorials/configs/operationsTutorial';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import fetchWithTenant from '../utils/fetchWithTenant';
import MdLicenseBanner from '../components/MdLicenseBanner';
import AlertRuleWizard from '../components/AlertRuleWizard';
import '../styles/v2/pages/md-operations.css';
import MdCredentials from './MdCredentials';

// ============================================
// SHARED UTILITIES
// ============================================
const COLORS = {
  primary: '#00d4aa',
  secondary: '#0ea5e9',
  warning: '#f59e0b',
  danger: '#ef4444',
  success: '#22c55e',
  muted: '#64748b',
  purple: '#a855f7',
};

// --- Version comparison helper ---
function compareVersions(a, b) {
  if (!a || !b) return 0;
  const pa = a.replace(/^v/, '').split('.').map(Number);
  const pb = b.replace(/^v/, '').split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const na = pa[i] || 0;
    const nb = pb[i] || 0;
    if (na > nb) return 1;
    if (na < nb) return -1;
  }
  return 0;
}


const formatRelativeTime = (date) => {
  if (!date) return 'Never';
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const SeverityBadge = ({ severity }) => {
  const config = {
    CRITICAL: { color: COLORS.danger, icon: XCircle },
    WARNING: { color: COLORS.warning, icon: AlertTriangle },
    INFO: { color: COLORS.secondary, icon: Bell },
  };
  const { color, icon: Icon } = config[severity] || config.INFO;
  return (
    <span className="md-severity-badge" style={{ background: `${color}18`, color, borderColor: `${color}30` }}>
      <Icon size={13} /> {severity}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="ops-stat-card">
    <div className="ops-stat-icon" style={{ background: `${accent}15`, color: accent }}>
      <Icon size={20} />
    </div>
    <div className="ops-stat-content">
      <span className="ops-stat-value">{value}</span>
      <span className="ops-stat-label">{label}</span>
    </div>
  </div>
);

// ============================================
// ALERTS TAB
// ============================================
const AlertsTab = ({ alerts, loading, onAcknowledge, onResolve, onRefresh }) => {
  const [filter, setFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const activeAlerts = alerts.filter(a => !a.resolvedAt);
  const criticalAlerts = alerts.filter(a => a.severity === 'CRITICAL' && !a.resolvedAt);
  const warningAlerts = alerts.filter(a => a.severity === 'WARNING' && !a.resolvedAt);
  const resolvedAlerts = alerts.filter(a => a.resolvedAt);

  const filtered = alerts.filter(a => {
    if (filter === 'ACTIVE') return !a.resolvedAt;
    if (filter === 'CRITICAL') return a.severity === 'CRITICAL' && !a.resolvedAt;
    if (filter === 'WARNING') return a.severity === 'WARNING' && !a.resolvedAt;
    if (filter === 'RESOLVED') return a.resolvedAt;
    return true; // null = show all
  }).filter(a => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (a.title || '').toLowerCase().includes(term) ||
           (a.message || '').toLowerCase().includes(term) ||
           (a.deviceName || '').toLowerCase().includes(term);
  });

  return (
    <div className="ops-tab-content">
      {/* Stats — clickable filters */}
      <div className="ops-stats-grid">
        <div className={`ops-stat-card clickable ${filter === 'ACTIVE' ? 'selected' : ''}`} onClick={() => setFilter(filter === 'ACTIVE' ? null : 'ACTIVE')}>
          <div className="ops-stat-icon" style={{ background: COLORS.danger + '15', color: COLORS.danger }}><Bell size={20} /></div>
          <div className="ops-stat-content"><span className="ops-stat-value">{activeAlerts.length}</span><span className="ops-stat-label">Active Alerts</span></div>
        </div>
        <div className={`ops-stat-card clickable ${filter === 'CRITICAL' ? 'selected' : ''}`} onClick={() => setFilter(filter === 'CRITICAL' ? null : 'CRITICAL')}>
          <div className="ops-stat-icon" style={{ background: COLORS.danger + '15', color: COLORS.danger }}><XCircle size={20} /></div>
          <div className="ops-stat-content"><span className="ops-stat-value">{criticalAlerts.length}</span><span className="ops-stat-label">Critical</span></div>
        </div>
        <div className={`ops-stat-card clickable ${filter === 'WARNING' ? 'selected' : ''}`} onClick={() => setFilter(filter === 'WARNING' ? null : 'WARNING')}>
          <div className="ops-stat-icon" style={{ background: COLORS.warning + '15', color: COLORS.warning }}><AlertTriangle size={20} /></div>
          <div className="ops-stat-content"><span className="ops-stat-value">{warningAlerts.length}</span><span className="ops-stat-label">Warnings</span></div>
        </div>
        <div className={`ops-stat-card clickable ${filter === 'RESOLVED' ? 'selected' : ''}`} onClick={() => setFilter(filter === 'RESOLVED' ? null : 'RESOLVED')}>
          <div className="ops-stat-icon" style={{ background: COLORS.success + '15', color: COLORS.success }}><CheckCircle size={20} /></div>
          <div className="ops-stat-content"><span className="ops-stat-value">{resolvedAlerts.length}</span><span className="ops-stat-label">Resolved (24h)</span></div>
        </div>
      </div>

      {/* Search */}
      <div className="ops-toolbar">
        <div></div>
        <div className="ops-search-wrapper">
          <Search size={14} />
          <input
            type="text"
            placeholder="Search alerts..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="ops-search-input"
          />
        </div>
      </div>

      {/* Alert List */}
      {filtered.length === 0 ? (
        <div className="ops-empty-state">
          <CheckCircle size={40} />
          <h3>No alerts found</h3>
          <p>{filter === null ? 'All systems operating normally' : 'No alerts match this filter'}</p>
        </div>
      ) : (
        <div className="ops-alerts-list">
          {filtered.map(alert => (
            <div
              key={alert.id}
              className={`ops-alert-card ${alert.resolvedAt ? 'resolved' : ''}`}
              style={{ borderLeftColor: COLORS[alert.severity === 'CRITICAL' ? 'danger' : alert.severity === 'WARNING' ? 'warning' : 'secondary'] }}
            >
              <div className="ops-alert-main">
                <div className="ops-alert-info">
                  <div className="ops-alert-header">
                    <span className="ops-alert-title">{alert.title || alert.deviceName || 'Alert'}</span>
                    <SeverityBadge severity={alert.severity} />
                    {alert.acknowledgedAt && !alert.resolvedAt && (
                      <span className="ops-acked-badge"><Check size={11} /> Acked</span>
                    )}
                  </div>
                  <div className="ops-alert-message">{alert.message}</div>
                  <div className="ops-alert-meta">
                    {alert.ruleName && (
                      <span><Shield size={11} /> {alert.ruleName}</span>
                    )}
                    {alert.deviceIp && (
                      <span><Target size={11} /> {alert.deviceIp}</span>
                    )}
                    <span><Clock size={11} /> {formatRelativeTime(alert.createdAt)}</span>
                  </div>
                </div>
                {!alert.resolvedAt && (
                  <div className="ops-alert-actions">
                    {!alert.acknowledgedAt && (
                      <button className="ops-action-btn" onClick={() => onAcknowledge(alert.id)}>
                        <Check size={14} /> Ack
                      </button>
                    )}
                    <button className="ops-action-btn resolve" onClick={() => onResolve(alert.id)}>
                      <CheckCircle size={14} /> Resolve
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// RULES TAB
// ============================================
const CONDITION_ICONS = {
  DEVICE_OFFLINE: WifiOff,
  HIGH_LATENCY: Activity,
  RTSP_STREAM_DOWN: Eye,
  PACKET_LOSS: Zap,
  SNMP_THRESHOLD: Settings,
  HTTP_STATUS: Link2,
};

const RulesTab = ({ rules, loading, onToggle, onDelete, onEdit, onCreateNew }) => {
  const [ruleFilter, setRuleFilter] = useState(null);
  const activeRules = rules.filter(r => r.enabled);
  const disabledRules = rules.filter(r => !r.enabled);
  const triggeredCount = rules.reduce((sum, r) => sum + (r._count?.alerts || r.alertCount || 0), 0);

  return (
    <div className="ops-tab-content">
      {/* Stats — clickable filters */}
      <div className="ops-stats-grid">
        <div className={`ops-stat-card clickable ${ruleFilter === null ? 'selected' : ''}`} onClick={() => setRuleFilter(null)}>
          <div className="ops-stat-icon" style={{ background: COLORS.secondary + '15', color: COLORS.secondary }}><Shield size={20} /></div>
          <div className="ops-stat-content"><span className="ops-stat-value">{rules.length}</span><span className="ops-stat-label">Total Rules</span></div>
        </div>
        <div className={`ops-stat-card clickable ${ruleFilter === 'ACTIVE' ? 'selected' : ''}`} onClick={() => setRuleFilter(ruleFilter === 'ACTIVE' ? null : 'ACTIVE')}>
          <div className="ops-stat-icon" style={{ background: COLORS.success + '15', color: COLORS.success }}><CheckCircle size={20} /></div>
          <div className="ops-stat-content"><span className="ops-stat-value">{activeRules.length}</span><span className="ops-stat-label">Active</span></div>
        </div>
        <div className={`ops-stat-card clickable ${ruleFilter === 'TRIGGERED' ? 'selected' : ''}`} onClick={() => setRuleFilter(ruleFilter === 'TRIGGERED' ? null : 'TRIGGERED')}>
          <div className="ops-stat-icon" style={{ background: COLORS.warning + '15', color: COLORS.warning }}><Zap size={20} /></div>
          <div className="ops-stat-content"><span className="ops-stat-value">{triggeredCount}</span><span className="ops-stat-label">Triggered</span></div>
        </div>
        <div className={`ops-stat-card clickable ${ruleFilter === 'DISABLED' ? 'selected' : ''}`} onClick={() => setRuleFilter(ruleFilter === 'DISABLED' ? null : 'DISABLED')}>
          <div className="ops-stat-icon" style={{ background: COLORS.muted + '15', color: COLORS.muted }}><Power size={20} /></div>
          <div className="ops-stat-content"><span className="ops-stat-value">{disabledRules.length}</span><span className="ops-stat-label">Disabled</span></div>
        </div>
      </div>

      {/* Create Button */}
      <div className="ops-toolbar">
        <div></div>
        <button className="btn btn-primary" onClick={onCreateNew}>
          <Plus size={15} /> Create Rule
        </button>
      </div>

      {/* Rules Grid */}
      {rules.length === 0 ? (
        <div className="ops-empty-state">
          <Shield size={40} />
          <h3>No alert rules configured</h3>
          <p>Create your first rule to start monitoring device health</p>
          <button className="btn btn-primary" onClick={onCreateNew}>
            <Plus size={15} /> Create Rule
          </button>
        </div>
      ) : (
        <div className="ops-rules-grid">
          {rules.filter(r => {
            if (ruleFilter === 'ACTIVE') return r.enabled;
            if (ruleFilter === 'DISABLED') return !r.enabled;
            if (ruleFilter === 'TRIGGERED') return (r._count?.alerts || r.alertCount || 0) > 0;
            return true;
          }).map(rule => {
            const CondIcon = CONDITION_ICONS[rule.conditionType] || AlertTriangle;
            const sevColor = rule.severity === 'CRITICAL' ? COLORS.danger
              : rule.severity === 'WARNING' ? COLORS.warning : COLORS.secondary;

            return (
              <div key={rule.id} className={`ops-rule-card ${!rule.enabled ? 'disabled' : ''}`}>
                {/* Rule Header */}
                <div className="ops-rule-header">
                  <div className="ops-rule-icon" style={{ background: `${sevColor}15`, color: sevColor }}>
                    <CondIcon size={20} />
                  </div>
                  <div className="ops-rule-info">
                    <span className="ops-rule-name">{rule.name}</span>
                    <span className="ops-rule-type">{(rule.conditionType || '').replace(/_/g, ' ')}</span>
                  </div>
                  <SeverityBadge severity={rule.severity} />
                </div>

                {/* Description */}
                {rule.description && (
                  <div className="ops-rule-desc">{rule.description}</div>
                )}

                {/* Meta */}
                <div className="ops-rule-meta">
                  <span><Zap size={11} /> {rule._count?.alerts || rule.alertCount || 0} triggered</span>
                  <span><Clock size={11} /> {rule.cooldownSeconds >= 60 ? Math.floor(rule.cooldownSeconds/60) + 'm' : (rule.cooldownSeconds || 300/60) + 's'} cooldown</span>
                  <div className="md-rule-notify-icons">
                    {rule.notifyInApp && <span title="In-App"><Bell size={13} /></span>}
                    {rule.notifyEmail && <span title="Email" className="notify-email"><Mail size={13} /></span>}
                    {rule.notifySlack && <span title="Slack" className="notify-slack"><Hash size={13} /></span>}
                    {rule.notifyWebhook && <span title="Webhook" className="notify-webhook"><Link2 size={13} /></span>}
                    {rule.notifyPush && <span title="Push" className="notify-push"><Smartphone size={13} /></span>}
                  </div>
                </div>

                {/* Actions */}
                <div className="ops-rule-actions">
                  <button
                    className="ops-action-btn"
                    onClick={() => onEdit(rule)}
                    title="Edit rule"
                  >
                    <Edit2 size={14} /> Edit
                  </button>
                  <button
                    className={`ops-action-btn ${rule.enabled ? '' : 'success'}`}
                    onClick={() => onToggle(rule.id, !rule.enabled)}
                    title={rule.enabled ? 'Disable' : 'Enable'}
                  >
                    {rule.enabled ? <><Pause size={14} /> Disable</> : <><Play size={14} /> Enable</>}
                  </button>
                  <button
                    className="ops-action-btn danger"
                    onClick={() => onDelete(rule)}
                    title="Delete rule"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================
// AGENTS TAB
// ============================================
const AgentsTab = ({ agents, loading, onPairNew, onDelete, onRegenCode, onRefresh }) => {
  const [showDownloads, setShowDownloads] = useState(false);
  const [agentDownloads, setAgentDownloads] = useState([]);
  const [agentVersion, setAgentVersion] = useState('');
  const [agentFilter, setAgentFilter] = useState(null);
  const [updatingAgents, setUpdatingAgents] = useState({});
  const [changelog, setChangelog] = useState([]);
  const [updateModal, setUpdateModal] = useState(null);

  // Fetch latest agent version on mount for update comparison
  useEffect(() => {
    fetchWithTenant('/api/md/agent/downloads').then(r => r.json()).then(data => {
      setAgentDownloads(data.downloads || []);
      setAgentVersion(data.version || '');
      setChangelog(data.changelog || []);
    }).catch(() => {});
  }, []);

  const confirmUpdateAgent = (agentId, agentName) => {
    setUpdateModal({ agentId, agentName });
  };

  const handleUpdateAgent = async (agentId, agentName) => {
    setUpdateModal(null);
    setUpdatingAgents(prev => ({ ...prev, [agentId]: 'sending' }));
    try {
      const res = await fetchWithTenant(`/api/md/agents/${agentId}/update`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success(`Update command sent to ${agentName} (v${data.version})`);
        setUpdatingAgents(prev => ({ ...prev, [agentId]: 'updating' }));
        setTimeout(() => {
          setUpdatingAgents(prev => { const n = {...prev}; delete n[agentId]; return n; });
          onRefresh();
        }, 60000);
      } else {
        toast.error(data.error || 'Failed to send update');
        setUpdatingAgents(prev => { const n = {...prev}; delete n[agentId]; return n; });
      }
    } catch (err) {
      toast.error('Failed to send update: ' + err.message);
      setUpdatingAgents(prev => { const n = {...prev}; delete n[agentId]; return n; });
    }
  };
  const onlineCount = agents.filter(a => a.isOnline || a.status === 'ONLINE').length;

  // --- Update availability tracking ---
  const outdatedAgents = agents.filter(a => {
    if (!a.version || !agentVersion) return false;
    return compareVersions(a.version, agentVersion) < 0;
  });
  const hasUpdates = outdatedAgents.length > 0;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="ops-tab-content">
      {/* Update Available Banner */}
      {hasUpdates && (
        <div className="agent-update-banner">
          <div className="agent-update-banner-content">
            <div className="agent-update-banner-icon">
              <ArrowUpCircle size={20} />
            </div>
            <div className="agent-update-banner-text">
              <strong>Agent Update Available</strong>
              <span>Version {agentVersion} is available. {outdatedAgents.length} agent{outdatedAgents.length > 1 ? 's' : ''} running older versions.</span>
            </div>
            <button
              className="btn btn-sm"
              style={{ background: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b', border: '1px solid rgba(245, 158, 11, 0.3)' }}
              onClick={() => setShowDownloads(true)}
            >
              <Download size={14} /> Download Update
            </button>
          </div>
        </div>
      )}

      {/* Stats — clickable filters */}
      <div className="ops-stats-grid">
        <div className={`ops-stat-card clickable ${agentFilter === null ? 'selected' : ''}`} onClick={() => setAgentFilter(null)}>
          <div className="ops-stat-icon" style={{ background: COLORS.secondary + '15', color: COLORS.secondary }}><Radio size={20} /></div>
          <div className="ops-stat-content"><span className="ops-stat-value">{agents.length}</span><span className="ops-stat-label">Total Agents</span></div>
        </div>
        <div className={`ops-stat-card clickable ${agentFilter === 'ONLINE' ? 'selected' : ''}`} onClick={() => setAgentFilter(agentFilter === 'ONLINE' ? null : 'ONLINE')}>
          <div className="ops-stat-icon" style={{ background: COLORS.success + '15', color: COLORS.success }}><Wifi size={20} /></div>
          <div className="ops-stat-content"><span className="ops-stat-value">{onlineCount}</span><span className="ops-stat-label">Online</span></div>
        </div>
        <div className={`ops-stat-card clickable ${agentFilter === 'OFFLINE' ? 'selected' : ''}`} onClick={() => setAgentFilter(agentFilter === 'OFFLINE' ? null : 'OFFLINE')}>
          <div className="ops-stat-icon" style={{ background: COLORS.danger + '15', color: COLORS.danger }}><WifiOff size={20} /></div>
          <div className="ops-stat-content"><span className="ops-stat-value">{agents.length - onlineCount}</span><span className="ops-stat-label">Offline</span></div>
        </div>
        <div className={`ops-stat-card clickable ${agentFilter === 'MONITORED' ? 'selected' : ''}`} onClick={() => setAgentFilter(agentFilter === 'MONITORED' ? null : 'MONITORED')}>
          <div className="ops-stat-icon" style={{ background: COLORS.primary + '15', color: COLORS.primary }}><Eye size={20} /></div>
          <div className="ops-stat-content"><span className="ops-stat-value">{agents.reduce((s, a) => s + (a._count?.discoveredDevices || 0), 0)}</span><span className="ops-stat-label">Devices Monitored</span></div>
        </div>
        {hasUpdates && (
          <div className={`ops-stat-card clickable ${agentFilter === 'OUTDATED' ? 'selected' : ''}`} style={{ borderColor: 'rgba(245, 158, 11, 0.3)' }} onClick={() => setAgentFilter(agentFilter === 'OUTDATED' ? null : 'OUTDATED')}>
            <div className="ops-stat-icon" style={{ background: COLORS.warning + '15', color: COLORS.warning }}><ArrowUpCircle size={20} /></div>
            <div className="ops-stat-content"><span className="ops-stat-value">{outdatedAgents.length}</span><span className="ops-stat-label">Updates Available</span></div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="ops-toolbar">
        <button
          className="btn btn-secondary"
          onClick={() => setShowDownloads(!showDownloads)}
        >
          <Download size={15} /> {showDownloads ? 'Hide Downloads' : 'Download Agent'}
        </button>
        <button className="btn btn-primary" onClick={onPairNew}>
          <Plus size={15} /> Pair New Agent
        </button>
      </div>

      {/* Download Section */}
      {showDownloads && !agentDownloads.length && (() => {
        fetchWithTenant('/api/md/agent/downloads').then(r => r.json()).then(data => {
          setAgentDownloads(data.downloads || []);
          setAgentVersion(data.version || '');
        }).catch(() => {});
        return null;
      })()}
      {showDownloads && (
        <div className="ops-downloads-section">
          <div className="ops-downloads-header">
            <h3><Download size={18} /> Download CCM Monitor Agent</h3>
            <p>Select the version for your operating system. After downloading, run the installer and enter your pairing code.</p>
          </div>
          {agentVersion && <p style={{color:'var(--text-secondary)',fontSize:'0.85rem',margin:'0 0 8px'}}>Agent Version: {agentVersion}</p>}
          <div className="ops-downloads-grid">
            {agentDownloads.filter(dl => dl.available).map(dl => (
              <a
              
                key={dl.platform}
                href={dl.downloadUrl}
                className="ops-download-card"
                download={dl.filename}
              >
                <span className="ops-download-icon">{dl.os === 'windows' ? '🪟' : dl.os === 'linux' ? '🐧' : '🍎'}</span>
                <div className="ops-download-info">
                  <strong>{dl.label}</strong>
                  <span>{dl.size ? (dl.size / 1048576).toFixed(1) + ' MB' : ''}</span>
                </div>
                <Download size={16} />
              </a>
            ))}
            {agentDownloads.length === 0 && <div style={{color:'var(--text-tertiary)',padding:'1rem'}}>Loading downloads...</div>}
          </div>
        </div>
      )}

      {/* Agent List */}
      {agents.length === 0 ? (
        <div className="ops-empty-state">
          <Radio size={40} />
          <h3>No agents deployed</h3>
          <p>Pair your first agent to start monitoring your network</p>
          <button className="btn btn-primary" onClick={onPairNew}>
            <Plus size={15} /> Pair New Agent
          </button>
        </div>
      ) : (
        <div className="ops-agents-list">
          {agents.filter(a => {
              if (agentFilter === 'ONLINE') return a.isOnline || a.status === 'ONLINE';
              if (agentFilter === 'OFFLINE') return !(a.isOnline || a.status === 'ONLINE');
              if (agentFilter === 'MONITORED') return (a._count?.discoveredDevices || 0) > 0;
              if (agentFilter === 'OUTDATED') return agentVersion && a.version && compareVersions(a.version, agentVersion) < 0;
              return true;
            }).map(agent => {
            const isOnline = agent.isOnline || agent.status === 'ONLINE';
            return (
              <div key={agent.id} className="ops-agent-card">
                <div className="ops-agent-main">
                  <div className={`ops-agent-status-icon ${isOnline ? 'online' : 'offline'}`}>
                    <Radio size={22} />
                  </div>
                  <div className="ops-agent-info">
                    <div className="ops-agent-name">{agent.name || 'Unnamed Agent'}</div>
                    <div className="ops-agent-sub">
                      {agent.ipAddress || 'No IP'} • v{agent.version || '?'}
                      {agentVersion && agent.version && compareVersions(agent.version, agentVersion) < 0 && (
                        <span className="agent-update-badge" title={`Update to v${agentVersion}`}>
                          <ArrowUpCircle size={12} /> Update Available
                        </span>
                      )}
                      {agentVersion && agent.version && compareVersions(agent.version, agentVersion) >= 0 && (
                        <span className="agent-current-badge">Up to date</span>
                      )}
                      {agent.description && <> • {agent.description}</>}
                    </div>
                  </div>
                  <span className={`ops-status-pill ${isOnline ? 'online' : 'offline'}`}>
                    ● {isOnline ? 'Online' : 'Offline'}
                  </span>
                </div>

                {/* Agent Details */}
                <div className="ops-agent-details">
                  <div className="ops-agent-detail">
                    <span className="ops-detail-label">Heartbeat</span>
                    <span className="ops-detail-value">{formatRelativeTime(agent.lastSeenAt)}</span>
                  </div>
                  <div className="ops-agent-detail">
                    <span className="ops-detail-label">Devices</span>
                    <span className="ops-detail-value">{agent._count?.discoveredDevices || 0}</span>
                  </div>
                  <div className="ops-agent-detail">
                    <span className="ops-detail-label">Paired</span>
                    <span className="ops-detail-value">{formatRelativeTime(agent.createdAt)}</span>
                  </div>
                  {agent.pairingCode && !isOnline && (
                    <div className="ops-agent-detail">
                      <span className="ops-detail-label">Pairing Code</span>
                      <span
                        className="ops-detail-value code"
                        onClick={() => copyToClipboard(agent.pairingCode)}
                        title="Click to copy"
                      >
                        {agent.pairingCode} <Copy size={12} />
                      </span>
                    </div>
                  )}
                </div>

                {/* IQ connector health (only when the agent reports it) */}
                {agent.systemInfo?.iq?.enabled && (
                  <div className="ops-detail-iq-block">
                    <h4 className="ops-detail-iq-heading">
                      <Activity size={14} /> IQ
                    </h4>
                    <div className="ops-detail-iq-grid">
                      <div className="ops-detail-iq-row">
                        <span className="ops-detail-iq-label">Reachable</span>
                        <span className={`ops-detail-iq-value ${agent.systemInfo.iq.reachable ? 'ok' : 'fail'}`}>
                          {agent.systemInfo.iq.reachable ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div className="ops-detail-iq-row">
                        <span className="ops-detail-iq-label">Auth</span>
                        <span className={`ops-detail-iq-value ${agent.systemInfo.iq.authOk ? 'ok' : 'fail'}`}>
                          {agent.systemInfo.iq.authOk ? 'OK' : 'Failed'}
                        </span>
                      </div>
                      {agent.systemInfo.iq.version && (
                        <div className="ops-detail-iq-row">
                          <span className="ops-detail-iq-label">API</span>
                          <span className="ops-detail-iq-value">{agent.systemInfo.iq.version}</span>
                        </div>
                      )}
                      {agent.systemInfo.iq.schemaVersion && (
                        <div className="ops-detail-iq-row">
                          <span className="ops-detail-iq-label">Schema</span>
                          <span className="ops-detail-iq-value">v{agent.systemInfo.iq.schemaVersion}</span>
                        </div>
                      )}
                      {agent.systemInfo.iq.lastCheckAt && (
                        <div className="ops-detail-iq-row">
                          <span className="ops-detail-iq-label">Last check</span>
                          <span className="ops-detail-iq-value">
                            {new Date(agent.systemInfo.iq.lastCheckAt).toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Agent Actions */}
                <div className="ops-agent-actions">
                  {!isOnline && agent.pairingCode && (
                    <button className="ops-action-btn" onClick={() => onRegenCode(agent.id)}>
                      <RefreshCw size={14} /> Regenerate Code
                    </button>
                  )}
                  {agentVersion && agent.version && compareVersions(agent.version, agentVersion) < 0 && (
                    <button
                      className="ops-action-btn"
                      style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', borderColor: 'rgba(245, 158, 11, 0.3)' }}
                      onClick={() => confirmUpdateAgent(agent.id, agent.name)}
                      disabled={!!updatingAgents[agent.id]}
                    >
                      {updatingAgents[agent.id] === 'sending' ? (
                        <><Loader2 size={14} className="md-spin" /> Sending...</>
                      ) : updatingAgents[agent.id] === 'updating' ? (
                        <><Loader2 size={14} className="md-spin" /> Updating...</>
                      ) : (
                        <><ArrowUpCircle size={14} /> Update to v{agentVersion}</>
                      )}
                    </button>
                  )}
                  <button className="ops-action-btn danger" onClick={() => onDelete(agent.id, agent.name)}>
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    {/* Update Confirmation Modal */}
    {updateModal && (
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setUpdateModal(null)}>
        <div style={{ background: 'var(--bg-surface)', border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", padding: "24px", maxWidth: "500px", width: "90%", maxHeight: "70vh", overflow: "auto" }} onClick={e => e.stopPropagation()}>
          <h3 style={{ color: "#f59e0b", margin: "0 0 4px", display: "flex", alignItems: "center", gap: "8px" }}>
            <ArrowUpCircle size={20} /> Update Agent
          </h3>
          <p style={{ color: 'var(--text-secondary)', margin: "0 0 16px", fontSize: "0.9rem" }}>
            {"Update "}<strong style={{ color: 'var(--text-primary)' }}>{updateModal.agentName}</strong>{" to v"}{agentVersion}
          </p>
          {changelog.length > 0 && (
            <div style={{ marginBottom: "16px" }}>
              <h4 style={{ color: 'var(--text-primary)', margin: "0 0 8px", fontSize: "0.85rem" }}>{"What's New"}</h4>
              {changelog.filter(c => {
                const agent = agents.find(a => a.id === updateModal.agentId);
                return agent?.version ? compareVersions(c.version, agent.version) > 0 : true;
              }).map(release => (
                <div key={release.version} style={{ marginBottom: "12px" }}>
                  <div style={{ color: "#10b981", fontSize: "0.8rem", fontWeight: 600, marginBottom: "4px" }}>
                    {"v" + release.version + " \u2014 " + release.date}
                  </div>
                  {release.changes.map((ch, i) => (
                    <div key={i} style={{ color: "#cbd5e1", fontSize: "0.8rem", padding: "2px 0 2px 12px", display: "flex", gap: "6px" }}>
                      <span style={{ color: ch.type === "feature" ? "#10b981" : ch.type === "fix" ? "#ef4444" : "#3b82f6", fontSize: "0.7rem", fontWeight: 600, textTransform: "uppercase", minWidth: "70px" }}>{ch.type}</span>
                      {ch.description}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          <p style={{ color: 'var(--text-tertiary)', fontSize: "0.8rem", margin: "0 0 16px" }}>
            The agent will download the new binary, verify its checksum, and restart automatically.
          </p>
          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            <button className="ops-action-btn" onClick={() => setUpdateModal(null)} style={{ padding: "8px 16px" }}>Cancel</button>
            <button className="ops-action-btn" style={{ padding: "8px 16px", background: "rgba(245,158,11,0.2)", color: "#f59e0b", borderColor: "rgba(245,158,11,0.4)" }} onClick={() => handleUpdateAgent(updateModal.agentId, updateModal.agentName)}>
              <ArrowUpCircle size={14} />{" Update Now"}
            </button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

// ============================================
// MAIN OPERATIONS PAGE
// ============================================
const MdOperations = () => {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(operationsTutorial);

  const [activeTab, setActiveTab] = useState('rules');
  const navigateOps = useNavigate();
  const [loading, setLoading] = useState(true);

  // Alerts state
  const [alerts, setAlerts] = useState([]);

  // Rules state
  const [rules, setRules] = useState([]);
  const [showWizard, setShowWizard] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  // Agents state
  const [agents, setAgents] = useState([]);
  const [showPairModal, setShowPairModal] = useState(false);
  const [pairName, setPairName] = useState('');
  const [pairDescription, setPairDescription] = useState('');
  const [pairRole, setPairRole] = useState('FULL');
  const [newAgent, setNewAgent] = useState(null);
  const [pairing, setPairing] = useState(false);

  // ---- FETCH FUNCTIONS ----
  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/md/alerts');
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || data || []);
      }
    } catch (error) {
      console.error('[MD Ops] Failed to fetch alerts:', error);
    }
  }, []);

  const fetchRules = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/md/alert-rules');
      if (res.ok) {
        const data = await res.json();
        setRules(data.rules || data || []);
      }
    } catch (error) {
      console.error('[MD Ops] Failed to fetch rules:', error);
    }
  }, []);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/md/agents');
      if (res.ok) {
        const data = await res.json();
        setAgents(data.agents || data || []);
      }
    } catch (error) {
      console.error('[MD Ops] Failed to fetch agents:', error);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchAlerts(), fetchRules(), fetchAgents()]);
    setLoading(false);
  }, [fetchAlerts, fetchRules, fetchAgents]);

  useEffect(() => {
    fetchAll();
    // Auto-refresh alerts every 30s
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, [fetchAll, fetchAlerts]);

  // ---- ALERT ACTIONS ----
  const acknowledgeAlert = async (alertId) => {
    try {
      const res = await fetchWithTenant(`/api/md/alerts/${alertId}/acknowledge`, { method: 'POST' });
      if (res.ok) {
        toast.success('Alert acknowledged');
        fetchAlerts();
      }
    } catch {
      toast.error('Failed to acknowledge alert');
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      const res = await fetchWithTenant(`/api/md/alerts/${alertId}/resolve`, { method: 'POST' });
      if (res.ok) {
        toast.success('Alert resolved');
        fetchAlerts();
      }
    } catch {
      toast.error('Failed to resolve alert');
    }
  };

  // ---- RULE ACTIONS ----
  const toggleRule = async (ruleId, enabled) => {
    try {
      const res = await fetchWithTenant(`/api/md/alert-rules/${ruleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (res.ok) {
        toast.success(`Rule ${enabled ? 'enabled' : 'disabled'}`);
        fetchRules();
      }
    } catch {
      toast.error('Failed to update rule');
    }
  };

  const deleteRule = async (rule) => {
    if (!confirm(`Delete rule "${rule.name}"? This cannot be undone.`)) return;
    try {
      const res = await fetchWithTenant(`/api/md/alert-rules/${rule.id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success(`Rule "${rule.name}" deleted`);
        fetchRules();
      }
    } catch {
      toast.error('Failed to delete rule');
    }
  };

  // ---- AGENT ACTIONS ----
  const pairAgent = async () => {
    if (!pairName.trim()) {
      toast.error('Please enter an agent name');
      return;
    }
    setPairing(true);
    try {
      const res = await fetchWithTenant('/api/md/agents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: pairName.trim(), description: pairDescription.trim(), role: pairRole }),
      });
      if (res.ok) {
        const data = await res.json();
        setNewAgent(data);
        toast.success('Agent created! Use the pairing code on your agent.');
        fetchAgents();
      } else {
        const err = await res.json();
        toast.error(err.error || 'Failed to create agent');
      }
    } catch {
      toast.error('Failed to create agent');
    } finally {
      setPairing(false);
    }
  };

  const deleteAgent = async (agentId, name) => {
    if (!confirm(`Delete agent "${name}"? This cannot be undone.`)) return;
    try {
      const res = await fetchWithTenant(`/api/md/agents/${agentId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success(`Agent "${name}" deleted`);
        fetchAgents();
      }
    } catch {
      toast.error('Failed to delete agent');
    }
  };

  const regenerateCode = async (agentId) => {
    try {
      const res = await fetchWithTenant(`/api/md/agents/${agentId}/regenerate-code`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(`New pairing code: ${data.pairingCode}`);
        fetchAgents();
      }
    } catch {
      toast.error('Failed to regenerate code');
    }
  };

  // ---- BADGE COUNTS ----
  const activeAlertCount = alerts.filter(a => !a.resolvedAt).length;
  const activeRuleCount = rules.filter(r => r.enabled).length;
  const onlineAgentCount = agents.filter(a => a.isOnline || a.status === 'ONLINE').length;

  const tabs = [
    { id: 'agents', label: 'Agents', icon: Radio, badge: `${onlineAgentCount}/${agents.length}`, badgeColor: onlineAgentCount > 0 ? COLORS.success : COLORS.muted },
    { id: 'rules', label: 'Rules', icon: Shield, badge: activeRuleCount, badgeColor: COLORS.secondary },
    { id: 'credentials', label: 'Credentials', icon: Settings, badge: null, badgeColor: COLORS.muted },
  ];

  if (loading) {
    return (
      <div className="md-page">
        <div className="md-loading">
          <Loader2 size={32} className="md-spin" />
          <span>Loading operations...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="md-page">
      {/* Page Header */}
      <div className="page-header-with-actions">
        <div className="page-header-content">
          <h1 className="page-title">
            <Settings size={22} />
            Monitor Settings
          </h1>
          <p className="page-subtitle">Manage agents, alert rules, and credentials</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={fetchAll}>
            <RefreshCw size={15} /> Refresh
          </button>
          <button className="tutorial-help-btn" onClick={() => startTutorial(operationsTutorial.module, operationsTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
        </div>
      </div>

      <MdLicenseBanner />

      {/* Tab Bar */}
      <div className="ops-tab-bar">
        {tabs.map(t => (
          <button
            key={t.id}
            className={`ops-tab ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            <t.icon size={16} />
            <span className="ops-tab-label">{t.label}</span>
            <span
              className="ops-tab-badge"
              style={{ background: `${t.badgeColor}20`, color: t.badgeColor }}
            >
              {t.badge}
            </span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'credentials' && (
        <div className="ops-tab-content">
          <MdCredentials embedded />
        </div>
      )}

      {activeTab === 'rules' && (
        <RulesTab
          rules={rules}
          loading={loading}
          onToggle={toggleRule}
          onDelete={deleteRule}
          onEdit={(rule) => { setEditingRule(rule); setShowWizard(true); }}
          onCreateNew={() => { setEditingRule(null); setShowWizard(true); }}
        />
      )}

      {activeTab === 'agents' && (
        <AgentsTab
          agents={agents}
          loading={loading}
          onPairNew={() => { setShowPairModal(true); setNewAgent(null); setPairName(''); setPairDescription(''); setPairRole('FULL'); }}
          onDelete={deleteAgent}
          onRegenCode={regenerateCode}
          onRefresh={fetchAgents}
        />
      )}

      {/* Wizard Modal for Rules */}
      {showWizard && (
        <AlertRuleWizard
          editingRule={editingRule}
          onClose={() => setShowWizard(false)}
          onComplete={() => {
            setShowWizard(false);
            setEditingRule(null);
            fetchRules();
          }}
        />
      )}

      {/* Pair Agent Modal */}
      {showPairModal && (
        <div className="ops-modal-overlay" onClick={() => setShowPairModal(false)}>
          <div className="ops-modal" onClick={e => e.stopPropagation()}>
            <div className="ops-modal-header">
              <h2><Radio size={20} /> {newAgent ? 'Agent Created' : 'Pair New Agent'}</h2>
              <button className="ops-modal-close" onClick={() => setShowPairModal(false)}>
                <XCircle size={20} />
              </button>
            </div>

            {newAgent ? (
              <div className="ops-modal-body">
                <div className="ops-pair-success">
                  <CheckCircle size={48} color={COLORS.success} />
                  <h3>Agent Ready to Pair</h3>
                  <p>Use this pairing code in your CCM Monitor agent:</p>
                  <div
                    className="ops-pairing-code"
                    onClick={() => {
                      navigator.clipboard.writeText(newAgent.pairingCode || newAgent.agent?.pairingCode);
                      toast.success('Copied!');
                    }}
                    title="Click to copy"
                  >
                    {newAgent.pairingCode || newAgent.agent?.pairingCode}
                    <Copy size={16} />
                  </div>
                  <p className="ops-pair-hint">This code expires in 24 hours</p>
                </div>
                <div className="ops-modal-actions">
                  <button className="btn btn-primary" onClick={() => setShowPairModal(false)}>Done</button>
                </div>
              </div>
            ) : (
              <div className="ops-modal-body">
                <div className="ops-form-field">
                  <label>Agent Name *</label>
                  <input
                    type="text"
                    className="ops-input"
                    placeholder="e.g., HQ-Agent-01"
                    value={pairName}
                    onChange={e => setPairName(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="ops-form-field">
                  <label>Description</label>
                  <input
                    type="text"
                    className="ops-input"
                    placeholder="e.g., Main office network monitoring"
                    value={pairDescription}
                    onChange={e => setPairDescription(e.target.value)}
                  />
                </div>
                <div className="ops-form-field">
                  <label>Role</label>
                  <select
                    className="ops-input"
                    value={pairRole}
                    onChange={e => setPairRole(e.target.value)}
                  >
                    <option value="FULL">Full monitoring</option>
                    <option value="HOST_COLLECTOR">Host collector only</option>
                  </select>
                </div>
                <div className="ops-modal-actions">
                  <button className="btn btn-secondary" onClick={() => setShowPairModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={pairAgent} disabled={pairing || !pairName.trim()}>
                    {pairing ? <><Loader2 size={14} className="md-spin" /> Pairing...</> : <><Plus size={14} /> Create Agent</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showPrompt && (
        <TutorialPrompt
          module={operationsTutorial.module}
          moduleName={operationsTutorial.moduleName}
          steps={operationsTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
};

export default MdOperations;
